import { useState, useRef, useEffect } from 'react'
import { Modal, Spin, Button, Message, Tooltip } from '@arco-design/web-react'
import { IconDownload, IconShareInternal, IconPlayArrow } from '@arco-design/web-react/icon'
import styles from './index.module.less'
import useLocale from '@/utils/useLocale'
import useGetMember from '@/hooks/useGetMember'
import useGoLogin from '@/hooks/useGoLogin'
import VideoAiButton from '../VideoAiButton'
import Link from 'next/link'
import { getDownloadVideoApi } from '@/api/common'
import { SUCCESS_CODE } from '@/utils/constants'
import useCurrentRegion from '@/hooks/useCurrentRegion'
import { Region } from '@/utils/region'

interface VideoModalProps {
  visible: boolean
  onCancel: () => void
  url: string
  videoId?: string
}

export const VideoModal = (props: VideoModalProps) => {
  const { visible, onCancel, url, videoId } = props
  const [loading, setLoading] = useState(true)
  const iframe = useRef<HTMLIFrameElement>(null)
  const t = useLocale()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const { isLogin } = useGetMember()
  const { goLogin } = useGoLogin()
  const [watchOnAppLoading, setWatchOnAppLoading] = useState(false)
  const [watchOnAppVisible, setWatchOnAppVisible] = useState(false)
  const [videoUrl, setVideoUrl] = useState('')
  const { region } = useCurrentRegion()
  const isSoutheastAsia =
    region === Region.VN || region === Region.MY || region === Region.PH || region === Region.TH || region === Region.ID

  const handleWatchOnApp = async () => {
    setWatchOnAppVisible(true)
    if (videoId) {
      try {
        setWatchOnAppLoading(true)
        const resData = await getDownloadVideoApi(videoId)
        if (resData.data.code === SUCCESS_CODE) {
          setVideoUrl(resData.data.data.video_url)
        } else {
          Message.error(resData.data.msg)
        }
        setWatchOnAppLoading(false)
      } catch (error) {
        Message.error('Error obtaining video address')
        setWatchOnAppLoading(false)
      }
    } else {
      Message.error('videoId is missing')
    }
  }

  const onload = () => {
    setLoading(false)
  }

  useEffect(() => {
    setLoading(true)
  }, [url])

  const handleDownloadVideo = async () => {
    if (!isLogin) {
      goLogin()
      return
    }

    if (videoId) {
      try {
        setDownloadLoading(true)
        const close = Message.loading(t['download_video_loading'] || 'Downloading...')
        const resData = await getDownloadVideoApi(videoId)
        if (resData.data.code === SUCCESS_CODE) {
          const videoUrl = resData.data.data.video_url
          try {
            // 尝试使用 fetch 下载（解决跨域问题）
            const response = await fetch(videoUrl)
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = blobUrl
            link.download = `video_${videoId}.mp4`
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            // 释放 blob URL
            window.URL.revokeObjectURL(blobUrl)
            close()
            Message.success(t['download_video_success'] || 'Download successful')
          } catch (fetchError) {
            // 如果 fetch 失败（可能是跨域），回退到直接打开链接
            const link = document.createElement('a')
            link.href = videoUrl
            link.download = `video_${videoId}.mp4`
            link.target = '_blank'
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            close()
            Message.success(t['download_video_success'] || 'Download successful')
          }
        } else {
          close()
          Message.error(resData.data.msg)
        }
        setDownloadLoading(false)
      } catch (error) {
        close()
        Message.error(t['download_video_error'])
        setDownloadLoading(false)
      }
    } else {
      Message.error(t['download_video_error'])
    }
  }

  return (
    <div>
      <Modal
        title={null}
        maskClosable={false}
        visible={visible}
        footer={null}
        onCancel={() => {
          onCancel?.()
        }}
        style={{ width: 420 }}
        className={styles['video-modal']}
        unmountOnExit
      >
        <div style={{ color: '#fff', paddingLeft: '6px', width: 330, paddingBottom: 4 }}>{t['network_desc']}</div>
        <div style={{ position: 'absolute', left: 380, top: 80 }}>
          <Link href={`/tool/ai/tiktok-video-script?videoId=${videoId}`}>
            <a target="_blank">
              <VideoAiButton />
            </a>
          </Link>
        </div>
        <div style={{ position: 'absolute', left: 365, top: 130 }}>
          <Tooltip content={t['ss_tips']}>
            <Button
              type="text"
              loading={downloadLoading}
              onClick={handleDownloadVideo}
              icon={<IconDownload />}
              style={{ color: '#fff' }}
              className={styles['download-btn']}
            >
              {t['download_video']}
            </Button>
          </Tooltip>
        </div>
        {isSoutheastAsia && (
          <div style={{ position: 'absolute', left: 365, top: 220 }}>
            <Tooltip content={t['watch_on_app_desc']}>
              <Button
                type="text"
                onClick={handleWatchOnApp}
                icon={<IconPlayArrow />}
                style={{ color: '#fff' }}
                className={styles['download-btn']}
              >
                {t['watch_on_app']}
              </Button>
            </Tooltip>
          </div>
        )}
        <div style={{ position: 'absolute', left: 365, top: 180 }}>
          <Button
            type="text"
            onClick={() => {
              window.open(`https://www.tiktok.com/@fediukstore/video/${videoId}`, '_blank')
            }}
            icon={<IconShareInternal />}
            style={{ color: '#fff' }}
            className={styles['download-btn']}
          >
            {t['watch_on_official_website']}
          </Button>
        </div>
        <Spin loading={loading}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <iframe
              ref={iframe}
              src={url}
              width="332px"
              height="776px"
              onLoad={onload}
              style={{ border: 0, borderRadius: 8, overflow: 'hidden' }}
            ></iframe>
          </div>
        </Spin>
        <Modal
          visible={watchOnAppVisible}
          onCancel={() => setWatchOnAppVisible(false)}
          closable={true}
          title={t['watch_on_app']}
          className="rounded-2xl w-[400px] h-[766px]"
          footer={null}
        >
          <Spin loading={watchOnAppLoading} block>
            <div className="h-full">
              <video src={videoUrl} width="100%" height="100%" controls />
            </div>
          </Spin>
        </Modal>
      </Modal>
    </div>
  )
}
